import { useState, useEffect, useCallback } from 'react';
import { BrowserProvider } from 'ethers';

const API_BASE = import.meta.env.VITE_SHIELD_API_URL || '';

interface ShieldData {
  shieldScore: number;
  totalShield: number;
  activeShield: number;
  cifersRedeemed: number;
  dailyRate: number;
  breakdown: {
    stablecoin: number;
    caps: number;
    social: number;
    hold: number;
    weighted: number;
  };
}

interface KatData {
  totalKatEarned: number;
  currentEpoch: number | null;
  latestDailyAmount: number;
  latestEpoch: number | null;
}

interface CiferEntry {
  id: number;
  status: 'pending' | 'delivered';
  tx_hash: string | null;
  delivered_at: number | null;
  created_at: number;
}

interface HistoryEntry {
  epoch: number;
  token: 'shield' | 'kat' | 'caps' | 'cifer';
  source: string;
  amount: number;
  tx_hash: string | null;
  created_at: number;
}

interface SocialData {
  twitter: { id: string; handle: string; linkedAt: number } | null;
  tasks: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    points: number;
    max_per_day: number;
    target_account: string | null;
  }>;
  completions: Array<{
    task_id: string;
    points_earned: number;
    verified: number;
  }>;
  todayPoints: number;
}

interface Mission {
  id: string;
  title: string;
  description: string;
  tweetUrl?: string;
  tweetId?: string;
  targetAccount?: string;
  actions: Array<'like' | 'follow' | 'comment' | 'share'>;
  points: {
    like: number;
    follow: number;
    comment: number;
    share: number;
  };
  maxCommentBonus: number;
  startsAt?: number;
  endsAt?: number;
  completions?: Array<{
    action: string;
    verified: number;
    aiScore?: number;
    points_earned: number;
  }>;
}

export function useShieldApi(address: string | null, provider?: BrowserProvider | null) {
  const [shield, setShield] = useState<ShieldData | null>(null);
  const [kat, setKat] = useState<KatData | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [social, setSocial] = useState<SocialData | null>(null);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [cifers, setCifers] = useState<CiferEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!address || !API_BASE) return;

    setLoading(true);
    try {
      const [shieldRes, katRes, historyRes, socialRes, missionsRes, ciferRes] = await Promise.all([
        fetch(`${API_BASE}/api/shield/${address}`).then((r) => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/kat/${address}`).then((r) => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/history/${address}?limit=100`).then((r) => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/social/${address}`).then((r) => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/missions?address=${address}`).then((r) => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/cifer/${address}`).then((r) => r.json()).catch(() => null),
      ]);

      if (shieldRes) setShield(shieldRes);
      if (katRes) setKat(katRes);
      if (historyRes?.history) setHistory(historyRes.history);
      if (socialRes) setSocial(socialRes);
      if (missionsRes?.missions) setMissions(missionsRes.missions);
      if (ciferRes?.cifers) setCifers(ciferRes.cifers);
    } catch (err) {
      console.error('Failed to fetch Shield API:', err);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Start Twitter OAuth — gets auth URL from API, returns it for redirect
  const startTwitterAuth = useCallback(async (): Promise<{ success: boolean; authUrl?: string; error?: string }> => {
    if (!address) return { success: false, error: 'Wallet not connected' };

    try {
      const res = await fetch(`${API_BASE}/api/social/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.toLowerCase() }),
      });
      const data = await res.json() as any;

      if (data.authUrl) {
        return { success: true, authUrl: data.authUrl };
      }
      return { success: false, error: data.error || 'Failed to start OAuth' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Auth failed' };
    }
  }, [address]);

  // Unlink Twitter
  const unlinkTwitter = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!address || !provider) return { success: false, error: 'Wallet not connected' };

    try {
      const message = `Unlink Twitter from Ternoa Shield for ${address.toLowerCase()}`;
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      const res = await fetch(`${API_BASE}/api/social/unlink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.toLowerCase(), signature, message }),
      });
      const data = await res.json() as any;

      if (data.success) {
        await fetchAll();
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to unlink' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Signature rejected' };
    }
  }, [address, provider, fetchAll]);

  // Submit task completion
  const submitTask = useCallback(async (taskId: string, proofUrl?: string): Promise<{ success: boolean; pointsEarned?: number; todayTotal?: number; error?: string }> => {
    if (!address) return { success: false, error: 'Wallet not connected' };

    try {
      const res = await fetch(`${API_BASE}/api/social/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.toLowerCase(), taskId, proofUrl }),
      });
      const data = await res.json() as any;

      if (data.success) {
        await fetchAll();
        return { success: true, pointsEarned: data.pointsEarned, todayTotal: data.todayTotal };
      }
      return { success: false, error: data.error || 'Failed to submit' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Submission failed' };
    }
  }, [address, fetchAll]);

  // Verify mission action — requires wallet signature to prove ownership
  const verifyMissionAction = useCallback(async (
    missionId: string,
    action: string,
    _unused?: string,
    commentText?: string
  ): Promise<{ success: boolean; pointsEarned?: number; aiScore?: number; error?: string }> => {
    if (!address || !provider) return { success: false, error: 'Wallet not connected' };

    try {
      // Sign a message to prove wallet ownership
      const message = `Verify mission ${missionId} action ${action} for ${address.toLowerCase()}`;
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      const res = await fetch(`${API_BASE}/api/missions/${missionId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.toLowerCase(), action, commentText, signature, message }),
      });
      const data = await res.json() as any;

      if (data.success) {
        await fetchAll();
        return { success: true, pointsEarned: data.pointsEarned, aiScore: data.aiScore };
      }
      return { success: false, error: data.error || 'Verification failed' };
    } catch (err: any) {
      if (err?.code === 'ACTION_REJECTED' || err?.message?.includes('rejected')) {
        return { success: false, error: 'Signature rejected — please sign to verify' };
      }
      return { success: false, error: err?.message || 'Verification failed' };
    }
  }, [address, provider, fetchAll]);

  // Redeem CIFER — requires wallet signature
  const redeemCifer = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!address || !provider) return { success: false, error: 'Wallet not connected' };

    try {
      const message = `Redeem CIFER for ${address.toLowerCase()}`;
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      const res = await fetch(`${API_BASE}/api/cifer/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.toLowerCase(), signature, message }),
      });
      const data = await res.json() as any;

      if (data.success) {
        await fetchAll();
        return { success: true };
      }
      return { success: false, error: data.error || 'Redeem failed' };
    } catch (err: any) {
      if (err?.code === 'ACTION_REJECTED' || err?.message?.includes('rejected')) {
        return { success: false, error: 'Signature rejected' };
      }
      return { success: false, error: err?.message || 'Redeem failed' };
    }
  }, [address, provider, fetchAll]);

  return {
    shield,
    kat,
    history,
    social,
    missions,
    cifers,
    loading,
    refresh: fetchAll,
    startTwitterAuth,
    unlinkTwitter,
    submitTask,
    verifyMissionAction,
    redeemCifer,
  };
}
